import moment from "moment";

import { Client } from "@/Common/Api/Client";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { CheckedListControl } from "@/Common/Components/Inputs/CheckedListControl";
import { MultiDateSelector } from "@/Common/Components/Inputs/MultiDateSelector";
import { SearchSettingsFooter } from "@/Common/Components/Settings/SearchSettingsFooter";
import { EmployeeHelper } from "@/Common/Helpers/EmployeeHelper";
import { SearchMenuHelper } from "@/Common/Helpers/SearchMenuHelper";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";
import { CheckboxUIModel } from "@/Common/Models/UI/Inputs/CheckboxUIModel";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { MessageSearchSettings } from "@/Communications/Models/Settings/MessageSearchSettings";
import { UserSettings } from "@/Globals/Models/UserSettings";
import { BaseCommunicationSettingsState } from "./BaseCommunicationSettingsState";

import "./MessageSearchSettingsContainer_OptimizedV2.css";

interface IMessageSettingsContainer_OptimizedV2Props {
    onSaveSettingsCallback: (model: MessageSettingsContainer_OptimizedV2State) => void;
    onCancelClick: () => void;
    onSettingsButtonClick: () => void;
    enableSaveSetting: boolean;

    savedState?: () => MessageSettingsContainer_OptimizedV2State;
}

class MessageSettingsContainer_OptimizedV2State extends BaseCommunicationSettingsState {
    public isAllSharedWithChecked: boolean;
    public sharedWithSearchText: string;
    public priorities: CheckboxUIModel[];
    public sharedWith: EmployeeUIModel[];

    public static getDefault(includeUserPreferences: boolean = true): MessageSettingsContainer_OptimizedV2State {
        const s = new MessageSettingsContainer_OptimizedV2State();

        s.setBaseDefault();
        s.isAllSharedWithChecked = false;
        s.isAllPrioritiesChecked = false;

        s.sharedWithSearchText = "";

        s.sharedWith = s.employeesGlobalCache
            .filter(e => cacheStore.EmployeeSharingMessages.contains(e.Id))
            .map(e => e.clone());

        s.sharedWith.mutate(x => x.Checked = false);

        if (includeUserPreferences)
            s.setSearchSettingsFromUserPreferences();

        return s;
    }

    public setSearchSettingsFromUserPreferences(): void {
        const setting = cacheStore.UserSettings.MessageSearchSettings;
        if (setting == null)
            return;

        this.setBaseSearchSettingsFromUserPreferences(setting);

        this.isAllSharedWithChecked = setting.IsAllSharedWithChecked || this.isAllSharedWithChecked;

        if (setting.SharedWithIds)
            this.sharedWith.mutate(s => s.Checked = setting.SharedWithIds.contains(s.Id));
    }
}

class MessageSettingsContainer_OptimizedV2 extends BaseReactComponent<IMessageSettingsContainer_OptimizedV2Props, MessageSettingsContainer_OptimizedV2State> {
    public constructor(props: IMessageSettingsContainer_OptimizedV2Props) {
        super(props);
        this.state = MessageSettingsContainer_OptimizedV2State.getDefault();
    }

    public componentDidMount(): void {
        const savedState = this.props.savedState?.();
        if (savedState) {
            this.setState({
                ...savedState,
                isSaveSettingChecked: false
            });
        }

        super.setOverlay("MessageSettingsContainer_OptimizedV2", {
            transparent: true,
            onClicked: () => this.onSaveSettings()
        });
    }

    public onSaveSettings(): void {
        communicationsStore.communicationsTabSavedState.messageSettingContainerSavedState = this.state;

        if (this.state.isSaveSettingChecked)
            this.saveUserSettings();

        this.props.onSaveSettingsCallback(this.state);
    }

    public render(): JSX.Element {
        return (
            <div className="filter-component filter-component-responsive" onClick={e => e && e.stopPropagation()}>
                <table className="f-content">
                    <thead>
                        <tr>
                            <th className="first">
                                <label className="uppercase">{cacheStore.GetString("DueDateLabel")}</label>
                            </th>
                            {this.state.sites.length > 1 && (
                                <th>
                                    <CheckboxControl
                                        id="sites_check"
                                        label={cacheStore.GetString("SitesLabel").toUpperCase()}
                                        checked={this.state.isAllSitesChecked}
                                        onChange={() => this.mutateState(
                                            s => {
                                                s.isAllSitesChecked = !s.isAllSitesChecked;
                                                s.sites.mutate(site => site.Checked = s.isAllSitesChecked);
                                            },
                                            () => this.handleSitesChanged()
                                        )}
                                    />
                                </th>
                            )}
                            <th>
                                <CheckboxControl
                                    id="clinicians_check"
                                    label={cacheStore.GetString("CliniciansLabel").toUpperCase()}
                                    checked={this.state.isAllCliniciansChecked}
                                    onChange={() => this.mutateState(s => {
                                        s.isAllCliniciansChecked = !s.isAllCliniciansChecked;
                                        s.clinicians.mutate(c => c.Checked = s.isAllCliniciansChecked);
                                    })}
                                />
                            </th>
                            <th>
                                <CheckboxControl
                                    id="shared_with"
                                    label={cacheStore.GetString("SharedWithLabel").toUpperCase()}
                                    checked={this.state.isAllSharedWithChecked}
                                    onChange={() => this.mutateState(s => {
                                        s.isAllSharedWithChecked = !s.isAllSharedWithChecked;
                                        s.sharedWith.mutate(sw => sw.Checked = s.isAllSharedWithChecked);
                                    })}
                                />
                            </th>
                            <th style={{ width: "120px" }}>
                                <CheckboxControl
                                    id="urgency_check"
                                    label={cacheStore.GetString("PriorityLabel").toUpperCase()}
                                    checked={this.state.isAllPrioritiesChecked}
                                    onChange={() => this.mutateState(s => {
                                        s.isAllPrioritiesChecked = !s.isAllPrioritiesChecked;
                                        s.priorities.mutate(p => p.Checked = s.isAllPrioritiesChecked);
                                    })}
                                />
                            </th>
                            <th className="last message-search-settings-sort-by-container">
                                <label className="uppercase">{cacheStore.GetString("SortByLabel")}</label>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td className="first">
                                <div style={{ width: "237px" }}>
                                    <MultiDateSelector
                                        id="message_dates"
                                        from={this.state.dateFrom}
                                        fromChange={date => this.mutateState(s => s.dateFrom = date && moment(date).startOf("day").toDate())}
                                        fromSetTodayLabel={cacheStore.GetString("TodayLabel")}
                                        to={this.state.dateTo}
                                        toChange={date => this.mutateState(s => s.dateTo = date && moment(date).endOf("day").toDate())}
                                        toSetTodayLabel={cacheStore.GetString("TodayLabel")}

                                    />
                                </div>
                            </td>
                            {this.state.sites.length > 1 && (
                                <td className="sites-column">
                                    <div style={{ maxHeight: "400px", minHeight: "400px", overflow: "auto" }}>
                                        <CheckedListControl
                                            DataSource={this.state.sites.toImmutable()}
                                            onChange={(isChecked, id) => this.mutateState(
                                                s => s.sites.mutate(site => site.Checked = isChecked, site => site.Id === id),
                                                () => this.handleSitesChanged()
                                            )}
                                            MaxTextLength={25}
                                        />
                                    </div>
                                </td>
                            )}
                            <td style={{ width: "227px" }}>
                                <input
                                    className="form-control"
                                    type="text"
                                    value={this.state.cliniciansSearchText}
                                    onChange={(e: any) => this.handleCliniciansFilterTextChanged(e.target.value)}
                                    placeholder={cacheStore.GetString("SearchLabel").toUpperCase()}
                                />
                                <div style={{ maxHeight: "400px", minHeight: "400px", overflow: cacheStore.ClinicSettings.EnableLazyRenderingFilterSettingsClinicians ? "hidden" : "auto" }}>
                                    <CheckedListControl
                                        DataSource={this.getFilteredClinicians().toImmutable()}
                                        idModifier="cliniciansMessage"
                                        onChange={(isChecked, id) => this.mutateState(
                                            s => s.clinicians.mutate(c => c.Checked = isChecked, c => c.Id === id)
                                        )}
                                        sort
                                        limitTextWidth
                                        enableLazyRendering
                                    />
                                </div>
                            </td>
                            <td>
                                <input
                                    className="form-control"
                                    type="text"
                                    value={this.state.sharedWithSearchText}
                                    onChange={(e: any) => this.handleCliniciansSharedFilterTextChanged(e.target.value)}
                                    placeholder={cacheStore.GetString("SearchLabel").toUpperCase()}
                                />
                                <div style={{ maxHeight: "400px", minHeight: "400px", overflow: "auto" }}>
                                    <CheckedListControl
                                        DataSource={this.getFilteredSharedWith().toImmutable()}
                                        idModifier="sharedWithMessage"
                                        onChange={(isChecked, id) =>
                                            this.mutateState(
                                                s => s.sharedWith.mutate(sw => sw.Checked = isChecked, sw => sw.Id === id)
                                            )
                                        }
                                    />
                                </div>
                            </td>
                            <td>
                                <CheckedListControl
                                    DataSource={this.state.priorities.toImmutable()}
                                    idModifier="PriorityMessage"
                                    onChange={(isChecked, id) =>
                                        this.mutateState(
                                            s => s.priorities.mutate(p => p.Checked = isChecked, p => p.Id === id)
                                        )
                                    }
                                    dataCy="messageSettings-priority"
                                />
                            </td>
                            <td className="last message-search-settings-sort-by-container">
                                <div>
                                    <div onClick={() => this.mutateState(s => s.orderByPriorityFirst = true)}>
                                        <label>
                                            <input type="radio" checked={this.state.orderByPriorityFirst} />
                                            <label style={{ paddingLeft: "5px" }}>{cacheStore.GetString("PriorityHighToLowLabel")}</label>
                                        </label>
                                    </div>
                                    <div onClick={() => this.mutateState(s => s.orderByPriorityFirst = false)}>
                                        <label>
                                            <input type="radio" checked={!this.state.orderByPriorityFirst} />
                                            <label className="captilize-first-letter" style={{ paddingLeft: "5px" }}>{cacheStore.GetString("MessageDateLabel").toLocaleLowerCase()}</label>
                                        </label>
                                    </div>

                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
                <SearchSettingsFooter
                    footerClassName="message-footer"
                    onClose={() => this.props.onCancelClick()}
                    onOk={() => this.onSaveSettings()}
                    onColumnOrderingButtonClick={() => this.props.onSettingsButtonClick()}
                    onResultsPerPageChange={size => this.mutateState(s => s.selectedPageSizeOption = size)}
                    selectedPageSizeOption={this.state.selectedPageSizeOption}
                    pageSizeOptions={this.state.pageSizeOptions}
                    isSaveSettingsChecked={this.state.isSaveSettingChecked}
                    onSaveSettingsCheckboxChange={this.props.enableSaveSetting ? value => this.mutateState(s => s.isSaveSettingChecked = value) : null}
                />
            </div>
        );
    }

    private handleCliniciansFilterTextChanged(text: string): void {
        this.mutateState(s => s.cliniciansSearchText = text);
    }

    private handleCliniciansSharedFilterTextChanged(text: string): void {
        this.mutateState(s => s.sharedWithSearchText = text);
    }

    private handleSitesChanged(): void {
        this.mutateState(s =>
            s.clinicians = EmployeeHelper
                .filterCliniciansByCheckedSites(s.employeesGlobalCache, s.sites)
        );
    }

    private getFilteredClinicians(): EmployeeUIModel[] {
        return EmployeeHelper
            .filterCliniciansBySearchText(this.state.clinicians, this.state.cliniciansSearchText);
    }

    private getFilteredSharedWith(): EmployeeUIModel[] {
        return SearchMenuHelper
            .FilterCliniciansByName(
                this.state.sharedWithSearchText,
                this.state.sharedWith.toImmutable()
            )
            .toArray();
    }

    private saveUserSettings(): void {
        const userSettings = new UserSettings();
        userSettings.MessageSearchSettings = MessageSearchSettings.FromModel(this.state);

        Client.Post(
            ServerOperations.Server.Myle_UpdateUserSettings,
            {
                UserSettings: userSettings,
                UserId: sessionStore.userId
            },
            () => { }
        );
        cacheStore.UserSettings.MessageSearchSettings = userSettings.MessageSearchSettings;
    }
}

export { MessageSettingsContainer_OptimizedV2, MessageSettingsContainer_OptimizedV2State };
